import { Dialog, Transition } from '@headlessui/react';
import { XIcon } from 'lucide-react';
import { Montserrat } from 'next/font/google';
import { Fragment } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { cn } from '@util/index';

const montserrat = Montserrat({
  subsets: ['latin'],
  weight: ['400', '500', '600', '700'],
});

type MobileDrawerProps = {
  show: boolean;
  dismiss: () => void;
  title?: string;
  children: React.ReactNode;
  headerExtraButton?: React.ReactNode;
  fullScreen?: boolean;
  maxHeight?: number;
  onScroll?: (e: React.UIEvent<HTMLDivElement>) => void;
  topRounded?: boolean;
  hideCloseButton?: boolean;
  headerClassName?: string;
};

export default function MobileDrawer({
  show,
  dismiss,
  title,
  children,
  headerExtraButton,
  fullScreen = false,
  maxHeight = 70,
  onScroll,
  topRounded = false,
  hideCloseButton = false,
  headerClassName,
}: MobileDrawerProps) {
  return (
    <Transition show={show} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-[150] overflow-hidden"
        onClose={dismiss}
      >
        <AnimatePresence>
          {show && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{
                duration: 0.3,
                ease: [0.25, 0.46, 0.45, 0.94],
              }}
              className="fixed inset-0 bg-black/50"
              onClick={dismiss}
            />
          )}
        </AnimatePresence>

        <div className="fixed inset-x-0 bottom-0 flex w-full">
          <AnimatePresence>
            {show && (
              <motion.div
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{
                  duration: 0.35,
                  ease: [0.25, 0.46, 0.45, 0.94],
                }}
                className="w-full"
              >
                <div
                  className={`w-full overflow-y-auto bg-white ${
                    montserrat.className
                  } text-black transition-[max-height] duration-300 ease-out ${
                    fullScreen ? 'min-h-[90vh] rounded-none' : ''
                  } ${
                    maxHeight === 70 || topRounded
                      ? 'rounded-t-[4rem]'
                      : 'rounded-none'
                  }`}
                  style={{ maxHeight: `${maxHeight}vh` }}
                  onScroll={onScroll}
                >
                  {(title || headerExtraButton || !hideCloseButton) && (
                    <div
                      className={cn(
                        'shadow-md/20 sticky top-0 z-10 flex w-full items-center justify-center border-b border-[#e6e5e5] bg-white pb-8 pt-8',
                        headerClassName
                      )}
                    >
                      {headerExtraButton && (
                        <div className="absolute left-8">
                          {headerExtraButton}
                        </div>
                      )}

                      {title && (
                        <h1 className="whitespace-nowrap text-[2.1rem] font-semibold">
                          {title}
                        </h1>
                      )}

                      {!hideCloseButton && (
                        <button
                          type="button"
                          onClick={dismiss}
                          className="absolute right-8 rounded-full bg-[#F6F6F6] p-4"
                        >
                          <XIcon className="h-[3rem] w-[3.3rem] text-zinc-600" />
                        </button>
                      )}
                    </div>
                  )}

                  <div className="w-full overflow-y-scroll">{children}</div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </Dialog>
    </Transition>
  );
}
